import React from "react";

import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { FiVideoOff, FiMicOff } from "react-icons/fi";
import { LocalVideoTrack, Participant, RemoteVideoTrack } from "twilio-video";

import useIsTrackSwitchedOff from "../hooks/useIsTrackSwitchedOff/useIsTrackSwitchedOff";
import useLocalAudioToggle from "../hooks/useLocalAudioToggle/useLocalAudioToggle";
import useParticipantIsReconnecting from "../hooks/useParticipantIsReconnecting/useParticipantIsReconnecting";
import usePublications from "../hooks/usePublications/usePublications";
import useTrack from "../hooks/useTrack/useTrack";

const useStyles = makeStyles((theme: any) =>
  createStyles({
    participantContainer: {
      height: "175px",
      position: "relative",
      border: `${theme.participantBorderWidth}px solid rgb(245, 248, 255)`,
      borderRadius: "8px",
      background: "black",
      marginBottom: "8px",
    },
    container: {
      display: "block",
      margin: "0 auto",
      alignItems: "center",
      overflow: "hidden",
      marginBottom: "0.5em",
      "& video": {
        filter: "none",
      },
      [theme.breakpoints.down("sm")]: {
        width: `${(theme.sidebarMobileHeight * 16) / 9}px`,
        marginRight: "8px",
        marginBottom: "0",
        fontSize: "10px",
        paddingTop: `${theme.sidebarMobileHeight - 2}px`,
      },
    },
    innerContainer: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
    },
    infoContainer: {
      position: "absolute",
      zIndex: 2,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      height: "100%",
      width: "100%",
      background: "transparent",
      top: 0,
    },
    noCameraAvailable: {
      height: "44px",
      width: "100%",
      position: "relative",
      backgroundColor: "#F8F9FC",
      borderRadius: "8px",
      display: "flex",
      marginBottom: "0.5em",
    },
    cameraOff: {
      backgroundColor: "rgba(156, 166, 175, 0.1)",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      top: "7px",
      left: "10px",
      padding: "8px",
      gap: "12px",
      height: "30px",
      "& .cameraOff": {
        fontFamily: "Manrope",
        fontWeight: 500,
        fontSize: 12,
        lineHeight: "16px",
        color: "#465869",
      },
    },
    microphoneOff: {
      backgroundColor: "rgba(156, 166, 175, 0.1)",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "8px",
      position: "absolute",
      top: "6px",
      right: "8px",
    },
    reconnectingContainer: {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "15px",
      background: "rgba(40, 42, 43, 0.75)",
      zIndex: 1,
    },
    screenShareIconContainer: {
      padding: "0.18em 0.3em",
      marginRight: "0.3em",
      display: "flex",
      "& path": {
        fill: "white",
      },
    },
    identity: {
      color: "white",
      padding: "0.18em 0.3em",
      margin: 0,
      display: "flex",
      alignItems: "center",
    },
    typeography: {
      color: "white",
      [theme.breakpoints.down("sm")]: {
        fontSize: "0.75rem",
      },
    },
    hideParticipant: {
      display: "none",
    },
    cursorPointer: {
      cursor: "pointer",
    },
  })
);

interface ParticipantInfoProps {
  participant: Participant;
  children: React.ReactNode;
  onClick?: () => void;
  isLocalParticipant?: boolean;
  hideParticipant?: boolean;
}

export default function ParticipantInfo({
  participant,
  onClick,
  children,
  hideParticipant,
}: ParticipantInfoProps) {
  const publications = usePublications(participant);

  const audioPublication = publications.find((p) => p.kind === "audio");
  const videoPublication = publications.find((p) => p.kind === "video");

  const [isAudioEnabled, toggleAudioEnabled] = useLocalAudioToggle();

  const isVideoEnabled = Boolean(videoPublication);

  const videoTrack = useTrack(videoPublication);
  const isVideoSwitchedOff = useIsTrackSwitchedOff(
    videoTrack as LocalVideoTrack | RemoteVideoTrack
  );

  const isParticipantReconnecting = useParticipantIsReconnecting(participant);

  const classes = useStyles();

  const { t: translate } = useTranslation();

  if (!isVideoEnabled || isVideoSwitchedOff) {
    return (
      <div className={classes.noCameraAvailable}>
        <div className={classes.cameraOff} data-cy="videoSurveyLocalVideoDisabledIcon">
          <FiVideoOff size={18} color="#475467" />

          <span className="cameraOff" data-cy="videoSurveyLocalVideoDisabledText">
            {translate("cameraOff")}
          </span>
        </div>

        {!isAudioEnabled && (
          <div className={classes.microphoneOff}>
            <FiMicOff size={18} color="#475467" data-cy="videoSurveyLocalAudioDisabledIcon" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={classes.participantContainer}>
      <div
        className={clsx(classes.container, {
          [classes.hideParticipant]: hideParticipant,
          [classes.cursorPointer]: Boolean(onClick),
        })}
        onClick={onClick}
        data-cy-participant={participant.identity}
      >
        <div className={classes.innerContainer}>
          {isParticipantReconnecting && (
            <div className={classes.reconnectingContainer}>
              <Typography variant="body1" className={classes.typeography}>
                Reconnecting...
              </Typography>
            </div>
          )}
          {children}
        </div>
      </div>

      {!isAudioEnabled && (
        <div className={classes.microphoneOff}>
          <FiMicOff size={18} color="#475467" />
        </div>
      )}
    </div>
  );
}
